/**
 * AuthCallback page — handles the OIDC authorization code callback.
 *
 * rule42-auth redirects here after the user authenticates. This page:
 *  1. Extracts the authorization code + state from the URL
 *  2. Exchanges the code for tokens (using the stored PKCE verifier)
 *  3. Navigates to the originally requested page (or home)
 *
 * @module apps/web/pages/AuthCallback
 * @since W2.9
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { handleCallback } from '../lib/auth.js';
import { useAuth } from '../contexts/auth.js';

type CallbackState =
  | { status: 'processing' }
  | { status: 'error'; message: string };

export function AuthCallbackPage() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState<CallbackState>({ status: 'processing' });

  useEffect(() => {
    let cancelled = false;

    async function exchange() {
      try {
        const { returnTo } = await handleCallback();
        if (cancelled) return;
        // Notify the auth context that a session now exists
        refresh();
        navigate(returnTo, { replace: true });
      } catch (err) {
        if (cancelled) return;
        setState({
          status: 'error',
          message: err instanceof Error ? err.message : 'Authentication failed',
        });
      }
    }

    void exchange();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (state.status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-space-950 p-4">
        <div className="card-glass max-w-md w-full p-8 animate-slide-up">
          <div className="mb-4 flex items-center justify-center w-12 h-12 rounded-full bg-red-900/30 border border-red-800 mx-auto">
            <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-lg font-semibold text-center text-slate-100 mb-2">Authentication failed</h1>
          <p className="text-sm text-slate-400 text-center mb-6">{state.message}</p>
          <a href="/login" className="btn-primary w-full text-center">
            Back to login
          </a>
        </div>
      </div>
    );
  }

  // Processing state
  return (
    <div className="min-h-screen flex items-center justify-center bg-space-950">
      <div className="flex flex-col items-center gap-4 animate-fade-in">
        <div className="w-8 h-8 border-2 border-brand-indigo border-t-transparent rounded-full animate-spin" />
        <p className="text-slate-400 text-sm">Completing sign-in…</p>
      </div>
    </div>
  );
}
